import * as React from 'react'
import { useSwipeable } from 'react-swipeable'

import { pushToEventGA4 } from '@thg-commerce/enterprise-metrics/src/data_layer/pushToDataLayer/utils'

import { CarouselReducerActions, CarouselReducerActionTypes } from '../types'

const MINIMUM_SWIPE_DISTANCE_REQUIRED = 75 // px

interface UseSwipeProps {
  dispatch: (action: CarouselReducerActions) => void
  onSwipe?: () => void
  minimumSwipeDistance?: number
  prevSlide: React.MutableRefObject<number>
  totalSlides: number
  carouselRef: React.RefObject<HTMLDivElement>
  enableWrapAround?: boolean
}

const remmoveTransform = (divRef: React.RefObject<HTMLDivElement>) => {
  setTimeout(() => {
    if (!divRef.current) return
    divRef.current.style.removeProperty('transform')
    divRef.current.style.transition = '0s'
  }, 300)
}

export const wrapSwipe = (
  prevSlide: number,
  nextSlide: number,
  totalSlides: number,
  divRef: React.RefObject<HTMLDivElement>,
  offset: number = 0,
) => {
  if (!divRef.current) return

  if (prevSlide === totalSlides - 1 && nextSlide === 0) {
    divRef.current.style.transition = '0.3s ease'
    divRef.current.style.transform = `translateX(-${
      (totalSlides + 1) * 100
    }%) translateX(${offset}px)`
    remmoveTransform(divRef)
  } else if (prevSlide === 0 && nextSlide === totalSlides - 1) {
    divRef.current.style.transition = '0.3s ease'
    divRef.current.style.transform = `translateX(0%) translateX(${offset}px)`
    remmoveTransform(divRef)
  } else {
    divRef.current.style.transition = '0.3s ease'
  }
}

export const useSwipe = ({
  dispatch,
  onSwipe,
  enableWrapAround,
  prevSlide,
  totalSlides,
  carouselRef,
}: UseSwipeProps) => {
  const isSwiping = React.useRef(false)
  const [swipeOffset, setSwipeOffset] = React.useState(0)

  const swipeHandlers = useSwipeable({
    onSwipeStart: (event) => {
      if (event.dir === 'Right' || event.dir === 'Left') {
        isSwiping.current = true
      }
    },
    onSwiping: (event) => {
      if (!isSwiping.current) {
        return
      }

      event.event.preventDefault()
      setSwipeOffset(event.deltaX)
    },
    onSwiped: () => {
      setSwipeOffset(0)
    },
    onSwipedLeft: () => {
      if (!isSwiping.current) {
        return
      }

      const nextSlide =
        prevSlide.current === totalSlides - 1 ? 0 : prevSlide.current + 1
      if (enableWrapAround && carouselRef) {
        wrapSwipe(prevSlide.current, nextSlide, totalSlides, carouselRef)
      }
      prevSlide.current = nextSlide

      dispatch({ type: CarouselReducerActionTypes.SlideRight } as const)
      isSwiping.current = false
      pushToEventGA4({
        event: 'product_image_transition',
        type: 'swipe',
      })
      onSwipe?.()
    },
    onSwipedRight: () => {
      if (!isSwiping.current) {
        return
      }

      const nextSlide =
        prevSlide.current === 0 ? totalSlides - 1 : prevSlide.current - 1
      if (enableWrapAround && carouselRef) {
        wrapSwipe(prevSlide.current, nextSlide, totalSlides, carouselRef)
      }
      prevSlide.current = nextSlide

      dispatch({ type: CarouselReducerActionTypes.SlideLeft } as const)
      isSwiping.current = false
      pushToEventGA4({
        event: 'product_image_transition',
        type: 'swipe',
      })
      onSwipe?.()
    },
    delta: {
      left: MINIMUM_SWIPE_DISTANCE_REQUIRED,
      right: MINIMUM_SWIPE_DISTANCE_REQUIRED,
    },
    touchEventOptions: {
      passive: false,
    },
  })

  return {
    swipeHandlers,
    swipeOffset,
  }
}
